import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Search, BookmarkPlus } from "lucide-react";

const FIELDS = [
  { key: "simple", label: "Simple explanation" },
  { key: "example", label: "Real example" },
  { key: "usage", label: "Common usage" },
  { key: "mistakes", label: "Mistakes to avoid" },
];

export default function Dictionary() {
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/dictionary").then((r) => setTerms(r.data)).catch(() => {}); }, []);

  const filtered = terms.filter((t) =>
    t.term.toLowerCase().includes(q.toLowerCase()) || t.full.toLowerCase().includes(q.toLowerCase()) ||
    t.definition.toLowerCase().includes(q.toLowerCase()) || t.category.toLowerCase().includes(q.toLowerCase())
  );

  const save = async (t) => {
    const { data } = await api.post("/saved", { type: "term", ref_id: t.term, title: t.term, meta: { full: t.full } });
    toast.success(data.saved ? "Saved" : "Removed");
  };

  return (
    <div className="space-y-8" data-testid="dictionary-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Business Dictionary</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Speak the language of business.</h1>
        <p className="text-slate-400 mt-2">Every term comes with a simple explanation, a real example, common usage and mistakes to avoid.</p>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input data-testid="dictionary-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms (CAC, SAFE, churn…)" className="eco-input pl-10" />
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        {filtered.map((t) => (
          <AccordionItem key={t.term} value={t.term} data-testid={`term-${t.term}`} className="eco-card border-white/10 px-5 overflow-hidden">
            <AccordionTrigger className="hover:no-underline py-5">
              <div className="flex items-center gap-3 text-left">
                <div>
                  <div className="font-display text-lg font-semibold text-white">{t.term}</div>
                  <div className="text-xs text-slate-500">{t.full}</div>
                </div>
                <span className="eco-tag ml-2">{t.category}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <p className="text-slate-300 mb-4">{t.definition}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{f.label}</div>
                    <div className="text-sm text-slate-300">{t[f.key]}</div>
                  </div>
                ))}
              </div>
              <button data-testid={`save-term-${t.term}`} onClick={() => save(t)} className="btn-secondary text-sm mt-4"><BookmarkPlus size={15} /> Save term</button>
            </AccordionContent>
          </AccordionItem>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-500 py-12">No terms match "{q}".</p>}
      </Accordion>
    </div>
  );
}
